<script setup>
import { onMounted, computed, ref, watch } from "vue";
import mapboxgl from "mapbox-gl";
import { navigate } from "astro:transitions/client";
import FicheSkeleton from "@components/interactif/ficheSkeleton.vue";


const departements_cibles = ref([]);
const codes_naf_cibles = ref(null);
const categories_cnc_cibles = ref([]);
const preview_entreprise = ref(null);
const displayedEntreprises = ref([]);
const entreprises = ref([]);
const { terme } = defineProps(['terme']);
const search_query = ref(terme ? terme : "");
const has_search = ref(false);

const getEntreprises = async () => {
      
      try {
            const response = await fetch(`/api/search?query=${search_query.value}`);
            const data = await response.json();
            entreprises.value = data.results || []; 
            has_search.value = true;          
      } catch (error) {
            console.error("Erreur lors de la recherche :", error);
            entreprises.value = [];
            has_search.value = true;
      }
}

const filteredEntreprises = computed(() => {
	let result = [...entreprises.value];
      if (search_query.value.length > 2) {
            result = result.filter((entreprise) => 
                  entreprise.Denomination.toLowerCase().includes(String(search_query.value).toLowerCase()) ||
                  entreprise.mots_cles?.includes(String(search_query.value).toLowerCase()) ||
                  entreprise.SIRET?.includes(String(search_query.value).toLowerCase())
            );
      }
      if(departements_cibles.value.length > 0) {
            result = result.filter((entreprise) => departements_cibles.value.includes(entreprise.departement.nom_departement));
      }
      if(codes_naf_cibles.value) {
            result = result.filter((entreprise) => entreprise.code_naf[0]?.Codes_APE_id.Code_NAF === codes_naf_cibles.value);
      }
      if(categories_cnc_cibles.value.length > 0) {
            result = result.filter((entreprise) => categories_cnc_cibles.value.includes(entreprise.categories_cnc[0]?.categorie_cnc_id.nom));
      }
	return result;
});

const noresult = computed(() => {
      return filteredEntreprises.value.length == 0 && has_search.value === true;
})
const departements = computed(() => {
	const departements = [...new Set(entreprises.value.map((entreprise) => entreprise.departement.nom_departement))];
	return departements.sort().map((departement) => {
		const count = entreprises.value.filter((entreprise) => entreprise.departement.nom_departement === departement).length;
		return {
			nom_departement: departement,
			count: count,
		};
	});
});

const codes_naf = computed(() => {
	const codes_naf = [
		...new Set(
			entreprises.value.map((entreprise) => ({
				code_naf: entreprise.code_naf[0]?.Codes_APE_id.Code_NAF,
				intitule: entreprise.code_naf[0]?.Codes_APE_id.Intitule_NAF,
			}))
		),
	];
	// add count to each code_naf
	codes_naf.forEach((code_naf) => {
		code_naf.count = entreprises.value.filter((entreprise) => entreprise.code_naf[0]?.Codes_APE_id.Code_NAF === code_naf.code_naf).length;
	});
	return codes_naf.filter((v, i, a) => a.findIndex((t) => t.code_naf === v.code_naf) === i);
});
const categories_cnc = computed(() => {
	const categories_cnc = [...new Set(entreprises.value.map((entreprise) => entreprise.categories_cnc[0]?.categorie_cnc_id.nom))];
	return categories_cnc.sort().map((categorie_cnc) => {
		const count = entreprises.value.filter((entreprise) => entreprise.categories_cnc[0]?.categorie_cnc_id.nom === categorie_cnc).length;
		return {
			nom: categorie_cnc,
			count: count,
		};
	});
});

const resetFilters = () => {
      departements_cibles.value = [];
      codes_naf_cibles.value = null;
      categories_cnc_cibles.value = [];
      search_query.value = "";
};
const mapContainer = ref(null);
let map = null;
onMounted(async () => {
      await getEntreprises();
	mapboxgl.accessToken = "pk.eyJ1IjoibGV6YXJkc2NyZWF0aW9uIiwiYSI6ImNsbWRrbzI4dDE0MGkzY2pyNWZpejZmbTkifQ.StShQ0XubA242gUva_rrKQ";
	map = new mapboxgl.Map({
		container: mapContainer.value, // Elément conteneur de la carte
            style: "mapbox://styles/mapbox/light-v11",  
		//style: "mapbox://styles/lezardscreation/clmhi4r9h006c01qt6l292p8t", // Style de la carte
		center: [2.3488, 48.8534], // Coordonnées initiales [lng, lat] - exemple avec Paris
		zoom: 9, // Zoom initial
		scrollZoom: false,
	});
      map.dragRotate.disable();
      map.scrollZoom.disable();
      map.addControl(new mapboxgl.NavigationControl());
	map.on("load", () => {
		updateMap(); // Appel initial à updateMap
	});
	// Attente que la carte soit chargée avant d'ajouter des marqueurs
});

watch(search_query, async () =>
{
      await getEntreprises;
})

watch(
	filteredEntreprises,
	() => {
		if (map) updateMap(); // Mise à jour de la carte quand filteredEntreprises change
	},
	{ deep: true }
);

const updateMap = () => {
	const entreprisesGeoJSON = {
		type: "FeatureCollection",
		features: filteredEntreprises.value.filter((entreprise) => entreprise.position_carte?.coordinates && !entreprise.cacher_adresse_sur_site_web).map((entreprise) => ({
			type: "Feature",
			properties: {
				title: entreprise.Denomination,
			},
			geometry: {
				type: "Point",
				coordinates: entreprise.position_carte.coordinates,
			},
		})),
	};
	if (map.getSource("entreprises")) {
		map.getSource("entreprises").setData(entreprisesGeoJSON);
	} else {
		map.addSource("entreprises", {
			type: "geojson",
			data: entreprisesGeoJSON,
			cluster: true,
			clusterMaxZoom: 14,
			clusterRadius: 40,
		});

		map.addLayer({
			id: "clusters",
			type: "circle",
			source: "entreprises",
			filter: ["has", "point_count"], // Ce filtre s'assure que ce layer n'affiche que les clusters
			paint: {
				// Utilisez des expressions Mapbox pour styliser les clusters selon le nombre de points qu'ils contiennent
				"circle-color": [
					"step",
					["get", "point_count"],
					"rgba(1, 49, 64, 0.3)", // Couleur pour les clusters avec moins de 100 points
					100,
					"rgba(1, 49, 64, .4)", // Couleur pour les clusters avec moins de 750 points
					750,
					"rgba(1, 49, 64, .5)", // Couleur pour les autres clusters
				],
				"circle-radius": [
					"step",
					["get", "point_count"],
					50, // Rayon pour les clusters avec moins de 100 points
					100,
					60, // Rayon pour les clusters avec moins de 750 points
					750,
					70, // Rayon pour les autres clusters
				],
			},
		});

		map.addLayer({
			id: "clusters2",
			type: "circle",
			source: "entreprises",
			filter: ["has", "point_count"], // Ce filtre s'assure que ce layer n'affiche que les clusters
			paint: {
				// Utilisez des expressions Mapbox pour styliser les clusters selon le nombre de points qu'ils contiennent
				"circle-color": [
					"step",
					["get", "point_count"],
					"rgba(1, 49, 64, 0.6)", // Couleur pour les clusters avec moins de 100 points
					20,
					"rgba(1, 49, 64, 0.6)", // Couleur pour les clusters avec moins de 750 points
					750,
					"rgba(1, 49, 64, 0.6)", // Couleur pour les autres clusters
				],
				"circle-radius": [
					"step",
					["get", "point_count"],
					12, // Rayon pour les clusters avec moins de 100 points
					100,
					12, // Rayon pour les clusters avec moins de 750 points
					750,
					12, // Rayon pour les autres clusters
				],
			},
		});
		map.addLayer({
			id: "entreprises",
			type: "circle",
			source: "entreprises",
			filter: ["!", ["has", "point_count"]],
			paint: {
				"circle-radius": 8,
				"circle-color": "#013140",
				"circle-stroke-width": 20,
				"circle-stroke-color": "rgba(207, 175, 117, 0.2)",
			},
		});
		map.addLayer({
			id: "cluster-count", // Identifiant unique pour le layer de comptage
			type: "symbol",
			source: "entreprises", // Doit correspondre à l'ID de la source des clusters
			filter: ["has", "point_count"], // Filtre pour s'assurer d'afficher seulement les clusters
			layout: {
				"text-field": "{point_count_abbreviated}", // Utilise un champ spécial qui contient le nombre de points dans le cluster
				"text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"], // Spécifie la police utilisée
				"text-size": 14, // Taille de la police
			},
			paint: {
				"text-color": "#ffffff", // Couleur du texte
			},
		});
      }
      const bounds = new mapboxgl.LngLatBounds();
      entreprisesGeoJSON.features.forEach((feature) => {
            bounds.extend(feature.geometry.coordinates);
      });
      
      if (bounds.isEmpty()) {
            console.warn('No bounds available to fit.');
      } else {
            map.fitBounds(bounds, {
                  padding: window.innerWidth <= 768 ? 30 : 100,
                  maxZoom: 11, 
            });
      }

      map.on("click", "clusters", function (e) {
            var features = map.queryRenderedFeatures(e.point, {
                  layers: ["clusters"],
            });
            var clusterId = features[0].properties.cluster_id;
            map.getSource("entreprises").getClusterExpansionZoom(clusterId, function (err, zoom) {
                  if (err) return;
                  map.easeTo({
                        center: features[0].geometry.coordinates,
                        zoom: zoom,
                  });
            });
      });

      // set preview_entreprise when click on a marker
      map.on("click", "entreprises", function (e) {
            const entreprise = filteredEntreprises.value.find((entreprise) => entreprise.Denomination === e.features[0].properties.title);
            preview_entreprise.value = entreprise;
      });

      // scroll to the top of the page 
      window.scrollTo({
            top: 0,
            behavior: "smooth",
      });


      // desactiver la rotation de la carte en 3D


      // Réactivez le scroll zoom quand la touche Ctrl est enfoncée
      window.addEventListener('keydown', function (event) {
            if (event.key === "Control") {
                  map.scrollZoom.enable();
            }
      });

      // Désactivez à nouveau le scroll zoom quand la touche Ctrl est relâchée
      window.addEventListener('keyup', function (event) {
            if (event.key === "Control") {
                  map.scrollZoom.disable();
            }
      });

};
</script>
<template>
      <section id="carte">
            <div ref="mapContainer" class="h-[60vh] lg:h-[80vh] w-full bg-gris"></div>
      </section>
      <div class="listing bg-bleu/10 relative z-50 px-10 pb-10 lg:pb-20">
            <div class="container mx-auto flex flex-col lg:flex-row lg:gap-6 bg-gris relative z-50">
                  <div class="gauche basis-4/12 -translate-y-16 lg:-translate-y-40 text-white">
                        <div class="2xl:sticky top-72 h-auto rounded-sm flex flex-col gap-4">
                              <aside id="preview_entreprise"
                                    :class="`${preview_entreprise != null ? '-translate-y-full opacity-100' : 'pointer-events-none translate-y-0 opacity-0'} bg-white z-10 absolute top-0 left-0 max-w-sm w-full h-auto overflow-y-auto shadow-md text-bleu transition-all duration-300`">
                                    <div class="close absolute z-[9999] top-5 right-5"
                                          @click="preview_entreprise = null">
                                          <i class="fa-regular fa-times"></i>
                                    </div>
                                    <div class="flex flex-col justify-between " v-if="preview_entreprise">
                                          <div v-if="preview_entreprise.image_a_la_une && preview_entreprise.image_a_la_une.id"
                                                class="image w-full aspect-video bg-slate-50 overflow-hidden flex-none cursor-pointer hover:saturate-150 transition-all duration-300 ease-in-out"
                                                @click="navigate(`/entreprises/${entreprise.slug}`)">
                                                <img loading="lazy" class="object-cover object-center w-full h-full"
                                                      :title="`${preview_entreprise.Denomination} est sur la plateforme web de mise en relation CNC`"
                                                      :src="`https://cnc.directus.app/assets/${preview_entreprise.image_a_la_une?.id}?fit=cover&width=500&quality=80&format=webp`"
                                                      :alt="preview_entreprise.Denomination" />
                                          </div>

                                          <div v-else-if="!preview_entreprise.image_a_la_une && preview_entreprise.logo && preview_entreprise.logo.id"
                                                class="image flex items-center justify-center w-full aspect-video bg-white overflow-hidden flex-none cursor-pointer hover:saturate-150 transition-all duration-300 ease-in-out">
                                                <img loading="lazy" class="object-contain object-center w-3/4 h-3/4"
                                                      :title="`${preview_entreprise.Denomination} est sur la plateforme web de mise en relation CNC`"
                                                      :src="`https://cnc.directus.app/assets/${preview_entreprise.logo.id}?fit=contain&width=500&quality=80&format=webp`"
                                                      :alt="preview_entreprise.Denomination" />
                                          </div>
                                          <div class="infos p-6 relative flex-1">
                                                <div class="tags mb-2 line-clamp-1 uppercase text-gray-400 text-xs"
                                                      v-if="preview_entreprise.categories_cnc">
                                                      {{ preview_entreprise.categories_cnc[0]?.categorie_cnc_id?.nom }}
                                                </div>
                                                <h3 class="text-xl font-medium capitalize"
                                                      :transition-name="`titre_fiche_${preview_entreprise.id}`">
                                                      {{ preview_entreprise.Denomination }}
                                                </h3>
                                                <address class="not-italic text-orange font-medium mb-4">
                                                      {{ preview_entreprise.ville }} ({{ preview_entreprise.Code_postal
                                                      }})
                                                </address>
                                                <div @click="navigate(`/entreprises/${preview_entreprise.slug}`)"
                                                      class="absolute cursor-pointer transition-all hover:bg-bleu aspect-square w-10 hover:w-14 flex items-center justify-center bottom-0 right-0 bg-orange text-white"
                                                      :title="`Voir la fiche de ${preview_entreprise.Denomination} sur la plateforme CNC`">
                                                      <i class="fa-regular fa-angle-right"></i>
                                                </div>
                                          </div>
                                    </div>
                              </aside>
                              <div class="bg-bleu p-6 shadow-xl z-50 rounded">
                                    <h1 class="sr-only lg:not-sr-only text-base font-bold lg:text-3xl lg:font-medium">
                                          Trouvez-vous entre
                                          professionnels</h1>
                                    <form method="get" action="/entreprises" 
                                          class="form-group group w-full -mt-2 lg:mt-6 border-b border-white/20 flex items-center relative justify-between group-has-[:focus]:border-orange">
                                          <button type="submit" class="hidden" />
                                          <input type="text"
                                                name="terme"
                                                :value="search_query"
                                                placeholder="Rechercher..."
                                                class="form-control w-full appearance-none bg-transparent outline-none ring-0 border-none focus:ring-0 placeholder:text-orange text-orange py-3 px-0" />
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22.16" height="22.16"
                                                      viewBox="0 0 22.16 22.16">
                                                      <path id="Icon_material-loupe" data-name="Icon material-loupe"
                                                            d="M15.188,8.54H12.972v4.432H8.54v2.216h4.432V19.62h2.216V15.188H19.62V12.972H15.188ZM14.08,3a11.08,11.08,0,0,0,0,22.16h8.864a2.223,2.223,0,0,0,2.216-2.216V14.08A11.1,11.1,0,0,0,14.08,3Zm0,19.944a8.864,8.864,0,1,1,8.864-8.864A8.876,8.876,0,0,1,14.08,22.944Z"
                                                            transform="translate(-3 -3)" fill="#cfaf75" />
                                                </svg>
                                    </form>
                                    <div class="filtres mt-6 lg:mt-10">
                                          <div class="text-orange text-sm">
                                                <input type="checkbox" id="filtres"
                                                      class="peer/filtres filtres_checkbox hidden" />
                                                <label for="filtres">Filtrez les résultats</label>
                                                <div
                                                      class="text-white peer-checked/filtres:opacity-100 peer-checked/filtres:h-auto peer-checked/filtres:pt-1 peer-checked/filtres:translate-y-0 h-0 overflow-hidden transition-all duration-300 translate-y-1/3 opacity-0 lg:h-auto lg:opacity-100 lg:translate-y-0">
                                                      <div class="zone relative group py-2 ">
                                                            <div class="head cursor-pointer hover:text-orange">
                                                                  <input type="checkbox" id="departement"
                                                                        class="hidden main" />
                                                                  <label for="departement"
                                                                        class="flex items-center justify-between">
                                                                        <span>Département</span>
                                                                        <i
                                                                              class="fa-regular fa-plus group-has-[.main:checked]:hidden"></i>
                                                                        <i
                                                                              class="fa-regular fa-minus hidden group-has-[.main:checked]:block"></i>
                                                                  </label>
                                                            </div>
                                                            <div class="group relative text-sm  overflow-auto no-scrollbar group-has-[.main:checked]:py-2 ">
                                                                  <div v-for="departement in departements"
                                                                        :key="departement.nom_departement"
                                                                        class="checkbox w-full overflow-hidden h-0 group-has-[.main:checked]:h-auto opacity-0 group-has-[.main:checked]:opacity-100 translate-y-1/3 transition-all group-has-[.main:checked]:translate-y-0 duration-300">
                                                                        <div
                                                                              class="relative flex px-1 items-start text-white w-full">
                                                                              <div class="flex h-6 items-center">
                                                                                    <input :id="departement.nom_departement"
                                                                                          aria-describedby="comments-description"
                                                                                          name="departement.nom_departement"
                                                                                          :value="departement.nom_departement"
                                                                                          type="checkbox"
                                                                                          v-model="departements_cibles"
                                                                                          class="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-600" />
                                                                              </div>
                                                                              <div
                                                                                    class="ml-3 text-sm leading-6 flex-auto">
                                                                                    <label :for="departement.nom_departement"
                                                                                          class="font-normal w-full text-white flex justify-between items-center">
                                                                                          {{ departement.nom_departement
                                                                                          }}
                                                                                          <span class="count">{{
                                                                                                departement.count
                                                                                                }}</span>
                                                                                    </label>
                                                                              </div>
                                                                        </div>
                                                                  </div>
                                                            </div>
                                                      </div>
                                                      <div class="zone relative group py-2 ">
                                                            <div class="head cursor-pointer hover:text-orange">
                                                                  <input type="checkbox" id="categoriecnc"
                                                                        class="hidden main" />
                                                                  <label for="categoriecnc"
                                                                        class="flex items center justify-between">
                                                                        <span>Catégorie CNC</span>
                                                                        <i
                                                                              class="fa-regular fa-plus group-has-[.main:checked]:hidden"></i>
                                                                        <i
                                                                              class="fa-regular fa-minus hidden group-has-[.main:checked]:block"></i>
                                                                  </label>
                                                            </div>
                                                            <div class="group max-h-44 overflow-auto no-scrollbar text-sm group-has-[.main:checked]:py-2">
                                                                  <div v-for="categorie_cnc in categories_cnc"
                                                                        :key="categorie_cnc"
                                                                        class="checkbox w-full overflow-hidden h-0 group-has-[.main:checked]:h-auto opacity-0 group-has-[.main:checked]:opacity-100 translate-y-1/3 transition-all group-has-[.main:checked]:translate-y-0 duration-300">
                                                                        <div
                                                                              class="relative flex px-1 items-start text-white w-full">
                                                                              <div class="flex h-6 items-center">
                                                                                    <input :id="categorie_cnc.nom"
                                                                                          aria-describedby="comments-description"
                                                                                          :name="categorie_cnc.nom"
                                                                                          :value="categorie_cnc.nom"
                                                                                          type="checkbox"
                                                                                          v-model="categories_cnc_cibles"
                                                                                          class="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-600" />
                                                                              </div>
                                                                              <div
                                                                                    class="ml-3 text-sm leading-6 flex-auto">
                                                                                    <label :for="categorie_cnc.nom"
                                                                                          class="font-normal w-full text-white flex justify-between items-center">
                                                                                          {{ categorie_cnc.nom }}
                                                                                          <span class="count">{{
                                                                                                categorie_cnc.count
                                                                                                }}</span>
                                                                                    </label>
                                                                              </div>
                                                                        </div>
                                                                  </div>
                                                            </div>
                                                      </div>
                                                      <div class="zone group py-2">
                                                            <div class="head cursor-pointer hover:text-orange">
                                                                  <input type="checkbox" id="codenaf"
                                                                        class="hidden main" />
                                                                  <label for="codenaf"
                                                                        class="flex items-center justify-between">
                                                                        <span>Code NAF</span>
                                                                        <i
                                                                              class="fa-regular fa-plus group-has-[.main:checked]:hidden"></i>
                                                                        <i
                                                                              class="fa-regular fa-minus hidden group-has-[.main:checked]:block"></i>
                                                                  </label>
                                                            </div>
                                                          
                                                            <div class="group text-sm group-has-[.main:checked]:py-2">
                                                                 
                                                                  <select id="codenaf"
                                                                        class="group-has-[.main:checked]:h-auto pointer-events-none group-has-[.main:checked]:pointer-events-auto  opacity-0 group-has-[.main:checked]:opacity-100 translate-y-1/3 transition-all group-has-[.main:checked]:translate-y-0 duration-300 mt-2 block w-full bg-bleu rounded-md border-0 py-1.5 pl-3 pr-10 text-white ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-orange-600 sm:text-sm sm:leading-6"
                                                                        name="codenaf" v-model="codes_naf_cibles">
                                                                        <option value="null">Choisissez un code NAF
                                                                        </option>
                                                                        <option v-for="code_naf in codes_naf"
                                                                              :key="code_naf.code_naf"
                                                                              :value="code_naf.code_naf">{{
                                                                              code_naf.code_naf }} - {{
                                                                              code_naf.intitule }}</option>
                                                                  </select>
                                                            </div>
                                                      </div>
                                                      <div
                                                            class="erase_filters mt-4 lg:mt-10 text-sm group-has-[#filtres:checked]:block">
                                                            <a href="/entreprises" 
                                                                  class="text-orange block text-center border py-2 border-orange cursor-pointer hover:bg-orange hover:text-white hover:-translate-y-1 hover:shadow-lg transition-all">
                                                                  Réinitialiser les filtres</a>
                                                      </div>
                                                </div>
                                          </div>
                                    </div> 
                              </div>
                              <a href="/contact/?objet=creation" class="text-bleu text-center text-base py-4 hidden lg:block">
                                    Rejoignez les entreprises de la filière bois présentes sur CNC
                                    <br /><br />
                                    <span
                                          class="bg-orange hover:bg-bleu hover:translate-y-2 text-sm rounded-sm shadow-bleu transition-all hover:shadow-xl hover:rounded-sm text-white uppercase py-3 px-5 inline-block">
                                          Je suis intéressé.e !
                                    </span>
                                    
                              </a>
                              <button @click="search">Rechercher</button>
                        </div>
                  </div>
                  <div v-if="noresult" class="listing basis-8/12">
                        <h2 class="text-xl  p-10 font-medium hidden lg:block" v-if="noresult">
                              Aucune entreprise ne correspond à votre recherche
                        </h2>
                        <div class="grid grid-cols-1  sm:grid-cols-2 gap-4 relative -translate-y-10 lg:translate-y-0" v-if="!has_search">
                              <FicheSkeleton />
                              <FicheSkeleton />
                              <FicheSkeleton />
                              <FicheSkeleton />
                        </div>
                  </div>
                  <div class="listing basis-8/12" v-if="filteredEntreprises.length > 0 && !noresult">
                        <h2 class="text-xl absolute -mt-12 font-medium hidden lg:block">Ces <span class="text-orange font-bold">{{filteredEntreprises.length }}</span> entreprises vont vous intéresser...</h2>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 relative -translate-y-10 lg:translate-y-0">
                              <a :href="`/entreprises/${entreprise.slug}`" v-for="entreprise in filteredEntreprises" :key="entreprise.id"
                                    class="flex hover:shadow-xl hover:transition-all hover:-translate-y-1 flex-col items-stretch bg-white w-full rounded-sm shadow-md cursor-pointer">
                                    <div v-if="entreprise.image_a_la_une && entreprise.image_a_la_une.id"
                                          class="image w-full aspect-video bg-slate-50 overflow-hidden flex-none cursor-pointer hover:saturate-150 transition-all duration-300 ease-in-out">
                                          <img loading="lazy" class="object-cover object-center w-full h-full"
                                                :title="`${entreprise.Denomination} est sur la plateforme web de mise en relation CNC`"
                                                :src="`https://cnc.directus.app/assets/${entreprise.image_a_la_une?.id}?fit=cover&width=500&quality=80&format=webp`"
                                                :alt="entreprise.Denomination" />
                                    </div>

                                    <div v-else-if="!entreprise.image_a_la_une && entreprise.logo && entreprise.logo.id"
                                          class="image flex items-center justify-center w-full aspect-video bg-white overflow-hidden flex-none cursor-pointer hover:saturate-150 transition-all duration-300 ease-in-out">
                                          <img loading="lazy" class="object-contain object-center w-3/4 h-3/4"
                                                :title="`${entreprise.Denomination} est sur la plateforme web de mise en relation CNC`"
                                                :src="`https://cnc.directus.app/assets/${entreprise.logo.id}?fit=contain&width=500&quality=80&format=webp`"
                                                :alt="entreprise.Denomination" />
                                    </div>


                                    <div class="infos p-6 relative flex-1 flex flex-col justify-end">
                                          <div class="tags mb-2 line-clamp-1 uppercase text-gray-400 text-xs"
                                                v-if="entreprise.categories_cnc">
                                                {{ entreprise.categories_cnc[0]?.categorie_cnc_id?.nom }}
                                          </div>
                                          <h3 class="text-2xl font-medium capitalize"
                                                :transition-name="`titre_fiche_${entreprise.id}`">
                                                {{ entreprise.Denomination }}
                                          </h3>
                                          <address class="not-italic text-orange font-medium">{{ entreprise.ville }} ({{
                                                entreprise.Code_postal }})</address>
                                          <div 
                                                class="absolute cursor-pointer transition-all duration-300 hover:bg-bleu aspect-square w-10 hover:w-14 flex items-center justify-center bottom-0 right-0 bg-orange text-white"
                                                :title="`Voir la fiche de ${entreprise.Denomination} sur la plateforme CNC`">
                                                <i class="fa-regular fa-angle-right"></i>
                                          </div>
                                    </div>
                              </a>
                        </div>
                  </div>
            </div>
      </div>
</template>
<style scoped>
.custom-marker {
	width: 18px;
	height: 18px;
	border: 6px solid white;
	border-radius: 50%;
	background-color: #000;
	cursor: pointer;
	z-index: 888;
}
</style>
